/**
 * IT Portal — Health Center tab.
 *
 * Renders the unified health snapshot returned by
 * GET /api/admin/health-center: every dependency or "thing that can
 * expire" displayed as a card grouped by category, with a global
 * "stoplight" header summarising the worst severity across all checks.
 *
 * Each card exposes a "Run now" button so the IT operator can
 * force-refresh a single dependency on demand (bypassing the 1h TTL
 * cache used by the orchestrator). Auto-refresh every 90s as long as
 * the tab is visible.
 */
import { useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  HeartPulse, RefreshCw, AlertTriangle, CheckCircle2, Info,
  CircleAlert, CircleHelp, Clock,
} from "lucide-react";
import { API } from "../../config";
import { useITPortalI18n } from "../../i18n/ITPortalI18nContext";
import { useActiveInterval, useDeferredEffect } from "../../hooks/useWalletEffects";
import { translateHealthCheck } from "../../i18n/healthCenterTranslator";

const fmt = (tpl, vars) =>
  tpl.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));

const buildSevMeta = (t) => ({
  ok:              { label: t.hc_sev_ok,             tone: "ok",      icon: CheckCircle2 },
  info:            { label: t.hc_sev_info,           tone: "info",    icon: Info },
  warn:            { label: t.hc_sev_warn,           tone: "warn",    icon: AlertTriangle },
  red:             { label: t.hc_sev_red,            tone: "red",     icon: CircleAlert },
  unknown:         { label: t.hc_sev_unknown,        tone: "neutral", icon: CircleHelp },
  not_configured:  { label: t.hc_sev_not_configured, tone: "neutral", icon: CircleHelp },
});

const TONE_CLASSES = {
  ok:      { border: "border-lojack-blue-500/40", bg: "bg-lojack-blue-500/5",  text: "text-lojack-blue-300", chip: "bg-lojack-blue-500/15 text-lojack-blue-300 border-lojack-blue-500/40" },
  info:    { border: "border-lojack-blue-400/30", bg: "bg-lojack-blue-500/5",  text: "text-lojack-blue-300", chip: "bg-lojack-blue-500/10 text-lojack-blue-300 border-lojack-blue-400/40" },
  warn:    { border: "border-lojack-clause-500/40",       bg: "bg-lojack-clause-500/5",        text: "text-lojack-clause-300",       chip: "bg-lojack-clause-500/15 text-lojack-clause-300 border-lojack-clause-500/40" },
  red:     { border: "border-red-500/40",         bg: "bg-red-500/5",          text: "text-red-300",         chip: "bg-red-500/15 text-red-300 border-red-500/40" },
  neutral: { border: "border-gray-700",           bg: "bg-[#0a0a0a]",          text: "text-gray-400",        chip: "bg-gray-700/40 text-gray-400 border-gray-700" },
};

const buildFormatRelative = (t) => (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return t.hc_time_in_moment;
  const s = Math.round(diff / 1000);
  if (s < 60) return fmt(t.hc_time_sec_ago, { n: s });
  if (s < 3600) return fmt(t.hc_time_min_ago, { n: Math.round(s / 60) });
  if (s < 86400) return fmt(t.hc_time_hours_ago, { n: Math.round(s / 3600) });
  return fmt(t.hc_time_days_ago, { n: Math.round(s / 86400) });
};

const buildFormatEta = (t) => (seconds) => {
  if (!seconds || seconds <= 0) return t.hc_eta_only_ondemand;
  if (seconds < 3600) return fmt(t.hc_eta_every_min, { n: Math.round(seconds / 60) });
  if (seconds < 86400) return fmt(t.hc_eta_every_hours, { n: Math.round(seconds / 3600) });
  return fmt(t.hc_eta_every_days, { n: Math.round(seconds / 86400) });
};


const HealthCheckCard = ({ check, onRunNow, running, t, sevMeta, formatRelative, formatEta }) => {
  const meta = sevMeta[check.severity] || sevMeta.unknown;
  const tone = TONE_CLASSES[meta.tone];
  const Icon = meta.icon;

  return (
    <div className={`rounded-xl border ${tone.border} ${tone.bg} p-4`} data-testid={`hc-card-${check.check_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-start gap-2 min-w-0">
          <Icon size={16} className={`${tone.text} shrink-0 mt-0.5`} />
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate">{check.name}</p>
            <p className={`${tone.text} text-xs mt-0.5`}>{check.title}</p>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border shrink-0 ${tone.chip}`}>
          {meta.label}
        </span>
      </div>

      {check.details && (
        <p className="text-gray-400 text-xs leading-relaxed mt-1.5">{check.details}</p>
      )}

      {check.metrics && Object.keys(check.metrics).length > 0 && (
        <div className="grid grid-cols-2 gap-1.5 mt-3">
          {Object.entries(check.metrics).slice(0, 6).map(([k, v]) => (
            <div key={k} className="bg-[#0a0a0a]/60 border border-gray-800/70 rounded-md px-2.5 py-1.5">
              <p className="text-gray-500 text-[9px] uppercase tracking-wider">{k}</p>
              <p className="text-white text-[11px] font-mono truncate" title={String(v)}>{String(v)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-800/60">
        <div className="flex items-center gap-1 text-gray-500 text-[10px]">
          <Clock size={9} />
          <span>{formatRelative(check.checked_at)}</span>
          <span className="text-gray-700 mx-1">·</span>
          <span>{formatEta(check.next_check_eta_seconds)}</span>
        </div>
        <button
          onClick={() => onRunNow(check.check_id)}
          disabled={running}
          className="px-2.5 py-1 bg-[#1a1a1a] hover:bg-[#222] disabled:opacity-50 border border-gray-700 hover:border-lojack-blue-500/50 rounded-md text-gray-300 hover:text-lojack-blue-300 text-[11px] flex items-center gap-1.5"
          data-testid={`hc-run-${check.check_id}`}
        >
          <RefreshCw size={10} className={running ? "animate-spin" : ""} /> {t.hc_run_now}
        </button>
      </div>
    </div>
  );
};


export const HealthCenterTab = () => {
  const { t, locale } = useITPortalI18n();
  const sevMeta = useMemo(() => buildSevMeta(t), [t]);
  const formatRelative = useMemo(() => buildFormatRelative(t), [t]);
  const formatEta = useMemo(() => buildFormatEta(t), [t]);

  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingIds, setRefreshingIds] = useState({});
  const tokenRef = useRef(null);

  const authHeaders = () => {
    if (!tokenRef.current) tokenRef.current = localStorage.getItem("it_token");
    return { Authorization: `Bearer ${tokenRef.current}` };
  };

  const fetchAll = async (force = false) => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/health-center?force=${force}`, { headers: authHeaders() });
      setSnapshot(r.data);
    } catch (e) {
      toast.error(`Health Center: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runNow = async (checkId) => {
    setRefreshingIds(prev => ({ ...prev, [checkId]: true }));
    try {
      const r = await axios.post(`${API}/admin/health-center/${checkId}/run-now`, {}, { headers: authHeaders() });
      // Patch the snapshot with the updated single-check result.
      setSnapshot(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          checks: prev.checks.map(c => c.check_id === checkId ? r.data : c),
        };
      });
      const translated = translateHealthCheck(r.data, locale);
      const sev = translated.severity;
      const label = sevMeta[sev]?.label || sev;
      const tone = (sev === "ok" || sev === "info") ? "success" : (sev === "red" || sev === "warn" ? "warning" : "info");
      toast[tone](`${translated.name}: ${label} — ${translated.title}`);
    } catch (e) {
      toast.error(`${t.hc_err_prefix}: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRefreshingIds(prev => {
        const next = { ...prev };
        delete next[checkId];
        return next;
      });
    }
  };

  // First load + auto-refresh every 90 seconds (cached server-side anyway)
  useDeferredEffect(() => { fetchAll(false); }, []);
  useActiveInterval(() => fetchAll(false), true, 90 * 1000);

  // Translate backend-provided strings to the active locale BEFORE grouping
  // (so category headers + cards both use the translated values).
  const translatedChecks = useMemo(() => {
    if (!snapshot?.checks) return [];
    return snapshot.checks.map(c => translateHealthCheck(c, locale));
  }, [snapshot, locale]);

  const grouped = useMemo(() => {
    if (!translatedChecks.length) return [];
    const groups = {};
    translatedChecks.forEach(c => {
      const cat = c.category || t.hc_other_category;
      (groups[cat] = groups[cat] || []).push(c);
    });
    return Object.entries(groups).map(([category, items]) => ({ category, items }));
  }, [translatedChecks, t.hc_other_category]);

  const overallTone = TONE_CLASSES[sevMeta[snapshot?.overall_severity]?.tone || "neutral"];
  const overallLabel = sevMeta[snapshot?.overall_severity]?.label || "—";
  const overallSummary = useMemo(() => {
    if (!snapshot?.checks) return "";
    const counts = snapshot.checks.reduce((acc, c) => {
      acc[c.severity] = (acc[c.severity] || 0) + 1;
      return acc;
    }, {});
    const parts = [];
    if (counts.red) parts.push(`${counts.red} ${counts.red > 1 ? t.hc_count_red_many : t.hc_count_red_one}`);
    if (counts.warn) parts.push(`${counts.warn} ${t.hc_count_warn}`);
    if (counts.info) parts.push(`${counts.info} ${t.hc_count_info}`);
    if (counts.ok) parts.push(`${counts.ok} ${counts.ok > 1 ? t.hc_count_ok_many : t.hc_count_ok_one}`);
    if (counts.unknown) parts.push(`${counts.unknown} ${t.hc_count_unknown}`);
    if (counts.not_configured) parts.push(`${counts.not_configured} ${counts.not_configured > 1 ? t.hc_count_not_configured_many : t.hc_count_not_configured_one}`);
    return parts.join(" · ");
  }, [snapshot, t]);

  return (
    <div className="space-y-5" data-testid="health-center-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <HeartPulse size={22} className="text-lojack-blue-400" />
            {t.hc_title}
          </h2>
          <p className="text-gray-400 text-sm mt-1 max-w-3xl">{t.hc_subtitle}</p>
        </div>
        <button
          onClick={() => fetchAll(true)}
          disabled={loading}
          className="px-4 py-2 bg-lojack-blue-500/15 hover:bg-lojack-blue-500/25 disabled:opacity-50 border border-lojack-blue-500/40 text-lojack-blue-300 text-sm rounded-lg flex items-center gap-2"
          data-testid="hc-refresh-all"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          {t.hc_refresh_all}
        </button>
      </div>

      {/* Stoplight summary */}
      {snapshot && (
        <div className={`rounded-2xl border ${overallTone.border} ${overallTone.bg} p-5`} data-testid="hc-overall">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-gray-500 text-[10px] uppercase tracking-wider">{t.hc_overall_status}</p>
              <p className={`text-2xl font-bold mt-1 ${overallTone.text}`}>{overallLabel}</p>
            </div>
            <div className="text-right">
              <p className="text-gray-400 text-xs">{overallSummary}</p>
              <p className="text-gray-600 text-[10px] mt-1">{t.hc_last_snapshot}: {snapshot.generated_at?.replace("T", " ").slice(0, 19)}Z</p>
            </div>
          </div>
        </div>
      )}

      {/* Groups */}
      {loading && !snapshot && (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      )}

      {grouped.map(({ category, items }) => (
        <div key={category} className="bg-[#111] border border-gray-800 rounded-2xl p-5" data-testid={`hc-group-${category}`}>
          <p className="text-gray-400 text-xs uppercase tracking-wider mb-4">{category}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map(c => (
              <HealthCheckCard
                key={c.check_id}
                check={c}
                onRunNow={runNow}
                running={!!refreshingIds[c.check_id]}
                t={t}
                sevMeta={sevMeta}
                formatRelative={formatRelative}
                formatEta={formatEta}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default HealthCenterTab;
